import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faReceipt } from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Card, Col, Row } from "@themesberg/react-bootstrap";
import ContentLoader from "react-content-loader";
import { useHistory } from "react-router-dom";

import { getVendorOrders } from "../api/vendor.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";

const INITIAL_PAGE_SIZE = 10;

const formatDateTime = (dateValue) => {
  if (!dateValue) return "N/A";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString();
};

const STATUS_VARIANTS = {
  pending: { bg: "secondary", text: "Pending" },
  completed: { bg: "warning", text: "Completed" },
  acknowledged: { bg: "success", text: "Acknowledged" },
  rejected: { bg: "danger", text: "Rejected" },
  cancelled: { bg: "dark", text: "Cancelled" }
};

const getStatusBadge = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  const config = STATUS_VARIANTS[normalizedStatus] || { bg: "light", text: status || "N/A" };

  return (
    <Badge bg={config.bg} className="px-3 py-2 text-uppercase small">
      {config.text}
    </Badge>
  );
};

const OrderCardSkeleton = () => (
  <Card border="light" className="shadow-sm h-100">
    <ContentLoader
      speed={2}
      viewBox="0 0 400 200"
      backgroundColor="#f3f3f3"
      foregroundColor="#e0e0e0"
    >
      <rect x="15" y="20" width="120" height="16" rx="4" />
      <rect x="320" y="20" width="65" height="28" rx="14" />
      <rect x="15" y="60" width="80" height="10" rx="2" />
      <rect x="15" y="80" width="120" height="12" rx="2" />
      <rect x="15" y="130" width="100" height="36" rx="4" />
    </ContentLoader>
  </Card>
);

export default () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const loadOrders = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getVendorOrders({ page: pageNum, limit: INITIAL_PAGE_SIZE });
      const responseData = response?.data || response || [];
      const allOrders = Array.isArray(responseData) ? responseData : [];
      const total = response?.pagination?.total || response?.total || allOrders.length;
      setTotalOrders(total);

      const startIndex = (pageNum - 1) * INITIAL_PAGE_SIZE;
      const endIndex = startIndex + INITIAL_PAGE_SIZE;
      const pageOrders = allOrders.slice(startIndex, endIndex);

      if (append) {
        setOrders(prev => [...prev, ...pageOrders]);
      } else {
        setOrders(pageOrders);
      }

      const shouldLoadMore = pageOrders.length >= INITIAL_PAGE_SIZE && endIndex < total;
      setHasMore(shouldLoadMore);

      setPage(pageNum);
    } catch (error) {
      showToast({ message: error.message || "Failed to load orders", variant: "danger" });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadOrders(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMoreRef.current || !hasMore) return;
      
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (documentHeight - scrollPosition < 200) {
        loadOrders(page + 1, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page]);

  return (
    <div className="py-4">
      <div className="mb-4 px-2">
        <h3 className="mb-1">My Orders</h3>
        <p className="text-muted mb-0 small">{totalOrders} orders placed</p>
      </div>
      {loading ? (
        <Row className="g-3">
          {[...Array(4)].map((_, i) => (
            <Col xs={12} lg={6} className="mb-3" key={i}>
              <OrderCardSkeleton />
            </Col>
          ))}
        </Row>
      ) : orders.length === 0 ? (
        <Card border="light" className="shadow-sm">
          <Card.Body className="text-center py-5 text-muted">No orders found.</Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {orders.map((order) => (
              <Col xs={12} lg={6} className="mb-3" key={order.id}>
                <Card border="light" className="shadow-sm h-100 vendor-order-card">
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
                        <h5 className="mb-0">Order #{order.id}</h5>
                      </div>
                      <div>{getStatusBadge(order.status)}</div>
                    </div>
                    <div className="text-muted fw-bold" style={{ fontWeight: '600' }}>Created At</div>
                    <div className="fw-semibold mb-2">{formatDateTime(order.created_at)}</div>
                    {order.total_amount && (
                      <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                        <span className="text-muted fw-bold" style={{ fontWeight: '600' }}>Total</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>${Number(order.total_amount).toFixed(2)}</span>
                      </div>
                    )}
                    <Button
                      variant="outline-primary"
                      className="mt-2"
                      onClick={() => history.push(Routes.MyOrderDetails.path.replace(":id", order.id))}
                    >
                      View Details
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          {loadingMore && (
            <Row className="g-3">
              {[...Array(2)].map((_, i) => (
                <Col xs={12} lg={6} className="mb-3" key={`loading-${i}`}>
                  <OrderCardSkeleton />
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};